// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { RelationshipType, RelationshipCreateDTO } from '../../types/relationship';
import type { ClientCreateDTO } from '../../types/client';
import type { PolicyCreateDTO, RelationshipType as PolicyRelationshipType } from '../../types/policy';
import type { EmailTemplate } from '../../types/invitation';

// Wizard step definitions
export const WIZARD_STEPS = [
  { id: 1, name: 'Create Relationship', description: 'Relationship type and client details' },
  { id: 2, name: 'Add Policies', description: 'Add policies to the relationship' },
] as const;

export type WizardStepId = typeof WIZARD_STEPS[number]['id'];

// Extended client data for wizard (includes temp ID for tracking before save)
export interface WizardClient extends Omit<ClientCreateDTO, 'status'> {
  tempId: string;
  isPrimary: boolean;
  status: 'prospect';
}

// Extended policy data for wizard
export interface WizardPolicy extends Omit<PolicyCreateDTO, 'status'> {
  tempId: string;
  providerName?: string;
}

// Policy signatory assignment for wizard
export interface WizardSignatory {
  policyTempId: string;
  clientTempId: string;
  relationshipType: PolicyRelationshipType;
}

// Step validation state
export interface StepValidation {
  isValid: boolean;
  errors: string[];
  touched: boolean;
}

// Completion state for after successful profile creation
export interface CompletionClient {
  clientId: string;
  firstName: string;
  lastName: string;
  email: string;
  isPrimary: boolean;
}

export interface CompletionState {
  profileId: string;
  profileName: string;
  createdClients: CompletionClient[];
  emailTemplate: EmailTemplate | null;
  emailTemplateLoading: boolean;
  customSubject: string;
  customBody: string;
  isEmailModified: boolean;
  sendingState: 'idle' | 'sending' | 'success' | 'error';
  sendingError: string | null;
}

// Full wizard state
export interface WizardState {
  currentStep: WizardStepId;
  profileData: Partial<RelationshipCreateDTO> & { profileType: RelationshipType };
  clients: WizardClient[];
  policies: WizardPolicy[];
  signatories: WizardSignatory[];
  stepValidation: Record<WizardStepId, StepValidation>;
  isSubmitting: boolean;
  submitError: string | null;
  // Track if Solo profile has unsaved form data (for Step 1 validation)
  hasSoloClientFormData: boolean;
  // Created profile/client IDs after step 1 submit
  createdProfileId: string | null;
  createdClientIds: Record<string, string>; // tempId -> real UUID
  // Completion state (set after successful profile creation)
  completionState: CompletionState | null;
}

// Derive profile name from clients
export function deriveProfileName(profileType: RelationshipType, clients: WizardClient[]): string {
  if (profileType === 'SOLO' && clients[0]) {
    return `${clients[0].firstName} ${clients[0].lastName}`.trim();
  }
  if (profileType === 'JOINT' && clients.length >= 2) {
    return `${clients[0].firstName} & ${clients[1].firstName}`.trim();
  }
  return '';
}

// Step action callback type - returns true if action succeeded, false to block navigation
type StepAction = () => boolean;

// Context interface
interface WizardContextType {
  state: WizardState;
  // Whether the legacy "Add Policies" step (step 2) is available for this
  // brokerage. When false the wizard is single-step and completes on the
  // invitation-email screen.
  policiesStepEnabled: boolean;
  // Navigation
  goToStep: (step: WizardStepId) => void;
  nextStep: () => void;
  prevStep: () => void;
  canNavigateToStep: (step: WizardStepId) => boolean;
  // Relationship
  updateProfileData: (data: Partial<RelationshipCreateDTO>) => void;
  // Clients
  addClient: (client: Omit<WizardClient, 'tempId'>) => string;
  updateClient: (tempId: string, data: Partial<WizardClient>) => void;
  removeClient: (tempId: string) => void;
  setPrimaryClient: (tempId: string) => void;
  // Solo profile form data tracking (for Step 2 validation)
  setSoloClientFormData: (hasData: boolean) => void;
  // Policies
  addPolicy: (policy: Omit<WizardPolicy, 'tempId'>) => string;
  updatePolicy: (tempId: string, data: Partial<WizardPolicy>) => void;
  removePolicy: (tempId: string) => void;
  // Signatories
  addSignatory: (signatory: WizardSignatory) => void;
  removeSignatory: (policyTempId: string, clientTempId: string) => void;
  updateSignatoryType: (policyTempId: string, clientTempId: string, relationshipType: PolicyRelationshipType) => void;
  getSignatoriesForPolicy: (policyTempId: string) => WizardSignatory[];
  // Validation
  validateStep: (step: WizardStepId) => StepValidation;
  markStepTouched: (step: WizardStepId) => void;
  // Step actions (for auto-save before navigation)
  registerBeforeLeaveAction: (step: WizardStepId, action: StepAction) => void;
  unregisterBeforeLeaveAction: (step: WizardStepId) => void;
  // Created profile (after step 1 submit)
  setCreatedProfile: (profileId: string, clientIdMappings: Record<string, string>) => void;
  // Submission
  setSubmitting: (isSubmitting: boolean) => void;
  setSubmitError: (error: string | null) => void;
  // Draft
  saveDraft: () => void;
  loadDraft: () => boolean;
  clearDraft: () => void;
  hasDraft: () => boolean;
  // Completion state
  setCompletionState: (state: CompletionState) => void;
  updateEmailContent: (subject: string, body: string) => void;
  resetEmailToDefault: () => void;
  setEmailTemplateLoading: (loading: boolean) => void;
  setEmailTemplate: (template: EmailTemplate) => void;
  setSendingState: (state: 'idle' | 'sending' | 'success' | 'error', error?: string | null) => void;
  // Reset
  resetWizard: () => void;
}

const DRAFT_STORAGE_KEY = 'loa_relationship_wizard_draft';
const DRAFT_EXPIRY_HOURS = 24;

// Generate unique temp ID
const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

// Initial state
const createInitialState = (): WizardState => ({
  currentStep: 1,
  profileData: {
    profileType: 'SOLO',
    profileName: '',
    notes: '',
    crmRelationshipNumber: '',
  },
  clients: [],
  policies: [],
  signatories: [],
  stepValidation: {
    1: { isValid: false, errors: [], touched: false },
    2: { isValid: true, errors: [], touched: false }, // Optional step (policies) - also final step
  },
  isSubmitting: false,
  submitError: null,
  hasSoloClientFormData: false,
  createdProfileId: null,
  createdClientIds: {},
  completionState: null,
});

const WizardContext = createContext<WizardContextType | null>(null);

export function WizardProvider({
  children,
  brokerageId,
  policiesStepEnabled = false,
}: Readonly<{ children: ReactNode; brokerageId: string; policiesStepEnabled?: boolean }>) {
  const [state, setState] = useState<WizardState>(createInitialState);
  const lastStep: WizardStepId = policiesStepEnabled ? 2 : 1;

  // Step actions that run before leaving a step (e.g., auto-save for Solo relationships)
  const stepActionsRef = useRef<Partial<Record<WizardStepId, StepAction>>>({});

  // Validation logic for each step
  const validateStep = useCallback((step: WizardStepId): StepValidation => {
    const errors: string[] = [];

    switch (step) {
      case 1: { // Create Relationship (relationship details + clients)
        if (!state.profileData.profileType) {
          errors.push('Relationship type is required');
        }

        const relationshipType = state.profileData.profileType;

        // Client validation (merged from old step 2)
        if (state.clients.length === 0) {
          if (relationshipType === 'SOLO' && state.hasSoloClientFormData) {
            // Solo profile with unsaved form data - allow, before-leave action will validate
            break;
          }
          errors.push('At least one client is required');
        } else {
          if (relationshipType === 'SOLO' && state.clients.length > 1) {
            errors.push('Solo relationship can only have 1 client');
          } else if (relationshipType === 'JOINT' && state.clients.length !== 2) {
            errors.push('Joint relationship must have exactly 2 clients');
          }
          // Check for duplicate emails
          const emails = state.clients.map(c => c.email.toLowerCase());
          const uniqueEmails = new Set(emails);
          if (emails.length !== uniqueEmails.size) {
            errors.push('Duplicate email addresses are not allowed');
          }
          // Check that exactly one primary exists
          const primaryCount = state.clients.filter(c => c.isPrimary).length;
          if (primaryCount !== 1) {
            errors.push('Exactly one client must be marked as primary');
          }
        }
        break;
      }

      case 2: { // Add Policies (optional but validate if any exist) - Final step
        // Policies are optional, but if added they should be valid
        for (const policy of state.policies) {
          if (!policy.providerId) {
            errors.push(`Policy requires a provider`);
          }
        }
        break;
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      touched: state.stepValidation[step]?.touched || false,
    };
  }, [state.profileData, state.clients, state.policies, state.stepValidation, state.hasSoloClientFormData]);

  // Navigation
  const canNavigateToStep = useCallback((step: WizardStepId): boolean => {
    // The Add Policies step is unreachable when the feature is off
    if (step > lastStep) return false;
    // Can always go back
    if (step < state.currentStep) return true;
    // Can only go forward if current step is valid
    if (step > state.currentStep) {
      for (let s = 1 as WizardStepId; s < step; s++) {
        const validation = validateStep(s);
        // Step 2 (Policies) is optional
        if (s !== 2 && !validation.isValid) {
          return false;
        }
      }
    }
    return true;
  }, [state.currentStep, validateStep, lastStep]);

  const goToStep = useCallback((step: WizardStepId) => {
    // Execute before-leave action for current step if one is registered
    const currentStep = state.currentStep;
    const beforeLeaveAction = stepActionsRef.current[currentStep];
    if (beforeLeaveAction) {
      const actionSucceeded = beforeLeaveAction();
      if (!actionSucceeded) {
        // Action failed (e.g., validation error), don't navigate
        return;
      }
    }

    if (canNavigateToStep(step)) {
      setState(prev => ({
        ...prev,
        currentStep: step,
        stepValidation: {
          ...prev.stepValidation,
          [prev.currentStep]: { ...validateStep(prev.currentStep), touched: true },
        },
      }));
    }
  }, [state.currentStep, canNavigateToStep, validateStep]);

  // Register a before-leave action for a step
  const registerBeforeLeaveAction = useCallback((step: WizardStepId, action: StepAction) => {
    stepActionsRef.current[step] = action;
  }, []);

  // Unregister a before-leave action for a step
  const unregisterBeforeLeaveAction = useCallback((step: WizardStepId) => {
    delete stepActionsRef.current[step];
  }, []);

  const nextStep = useCallback(() => {
    const next = Math.min(state.currentStep + 1, lastStep) as WizardStepId;
    goToStep(next);
  }, [state.currentStep, goToStep, lastStep]);

  const prevStep = useCallback(() => {
    const prev = Math.max(state.currentStep - 1, 1) as WizardStepId;
    goToStep(prev);
  }, [state.currentStep, goToStep]);

  // Relationship management
  const updateProfileData = useCallback((data: Partial<RelationshipCreateDTO>) => {
    setState(prev => ({
      ...prev,
      profileData: { ...prev.profileData, ...data },
    }));
  }, []);

  // Client management
  const addClient = useCallback((client: Omit<WizardClient, 'tempId'>): string => {
    const tempId = generateTempId();
    setState(prev => ({
      ...prev,
      clients: [...prev.clients, { ...client, tempId }],
    }));
    return tempId;
  }, []);

  const updateClient = useCallback((tempId: string, data: Partial<WizardClient>) => {
    setState(prev => ({
      ...prev,
      clients: prev.clients.map(c =>
        c.tempId === tempId ? { ...c, ...data } : c
      ),
    }));
  }, []);

  const removeClient = useCallback((tempId: string) => {
    setState(prev => {
      const newClients = prev.clients.filter(c => c.tempId !== tempId);
      // Also remove any signatories for this client
      const newSignatories = prev.signatories.filter(s => s.clientTempId !== tempId);
      // If removed client was primary and others exist, make first one primary
      if (newClients.length > 0 && !newClients.some(c => c.isPrimary)) {
        newClients[0].isPrimary = true;
      }
      return {
        ...prev,
        clients: newClients,
        signatories: newSignatories,
      };
    });
  }, []);

  const setPrimaryClient = useCallback((tempId: string) => {
    setState(prev => ({
      ...prev,
      clients: prev.clients.map(c => ({
        ...c,
        isPrimary: c.tempId === tempId,
      })),
    }));
  }, []);

  // Track Solo profile form data state
  const setSoloClientFormData = useCallback((hasData: boolean) => {
    setState(prev => ({
      ...prev,
      hasSoloClientFormData: hasData,
    }));
  }, []);

  // Created profile management
  const setCreatedProfile = useCallback((profileId: string, clientIdMappings: Record<string, string>) => {
    setState(prev => ({
      ...prev,
      createdProfileId: profileId,
      createdClientIds: clientIdMappings,
    }));
  }, []);

  // Policy management
  const addPolicy = useCallback((policy: Omit<WizardPolicy, 'tempId'>): string => {
    const tempId = generateTempId();
    setState(prev => ({
      ...prev,
      policies: [...prev.policies, { ...policy, tempId }],
    }));
    return tempId;
  }, []);

  const updatePolicy = useCallback((tempId: string, data: Partial<WizardPolicy>) => {
    setState(prev => ({
      ...prev,
      policies: prev.policies.map(p =>
        p.tempId === tempId ? { ...p, ...data } : p
      ),
    }));
  }, []);

  const removePolicy = useCallback((tempId: string) => {
    setState(prev => ({
      ...prev,
      policies: prev.policies.filter(p => p.tempId !== tempId),
      // Also remove signatories for this policy
      signatories: prev.signatories.filter(s => s.policyTempId !== tempId),
    }));
  }, []);

  // Signatory management
  const addSignatory = useCallback((signatory: WizardSignatory) => {
    setState(prev => {
      // Check if this combination already exists
      const exists = prev.signatories.some(
        s => s.policyTempId === signatory.policyTempId && s.clientTempId === signatory.clientTempId
      );
      if (exists) {
        // Update existing instead
        return {
          ...prev,
          signatories: prev.signatories.map(s =>
            s.policyTempId === signatory.policyTempId && s.clientTempId === signatory.clientTempId
              ? signatory
              : s
          ),
        };
      }
      return {
        ...prev,
        signatories: [...prev.signatories, signatory],
      };
    });
  }, []);

  const removeSignatory = useCallback((policyTempId: string, clientTempId: string) => {
    setState(prev => ({
      ...prev,
      signatories: prev.signatories.filter(
        s => !(s.policyTempId === policyTempId && s.clientTempId === clientTempId)
      ),
    }));
  }, []);

  const updateSignatoryType = useCallback((policyTempId: string, clientTempId: string, relationshipType: PolicyRelationshipType) => {
    setState(prev => ({
      ...prev,
      signatories: prev.signatories.map(s =>
        s.policyTempId === policyTempId && s.clientTempId === clientTempId
          ? { ...s, relationshipType }
          : s
      ),
    }));
  }, []);

  const getSignatoriesForPolicy = useCallback((policyTempId: string): WizardSignatory[] => {
    return state.signatories.filter(s => s.policyTempId === policyTempId);
  }, [state.signatories]);

  // Validation helpers
  const markStepTouched = useCallback((step: WizardStepId) => {
    setState(prev => ({
      ...prev,
      stepValidation: {
        ...prev.stepValidation,
        [step]: { ...prev.stepValidation[step], touched: true },
      },
    }));
  }, []);

  // Submission state
  const setSubmitting = useCallback((isSubmitting: boolean) => {
    setState(prev => ({ ...prev, isSubmitting }));
  }, []);

  const setSubmitError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, submitError: error }));
  }, []);

  // Draft persistence
  const saveDraft = useCallback(() => {
    const draft = {
      timestamp: Date.now(),
      brokerageId,
      state: {
        profileData: state.profileData,
        clients: state.clients,
        policies: state.policies,
        signatories: state.signatories,
        currentStep: state.currentStep,
      },
    };
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  }, [brokerageId, state.profileData, state.clients, state.policies, state.signatories, state.currentStep]);

  const loadDraft = useCallback((): boolean => {
    try {
      const draftJson = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!draftJson) return false;

      const draft = JSON.parse(draftJson);
      const isExpired = Date.now() - draft.timestamp > DRAFT_EXPIRY_HOURS * 60 * 60 * 1000;
      const isDifferentBrokerage = draft.brokerageId !== brokerageId;

      if (isExpired || isDifferentBrokerage) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        return false;
      }

      setState(prev => ({
        ...prev,
        profileData: draft.state.profileData,
        clients: draft.state.clients,
        policies: draft.state.policies,
        signatories: draft.state.signatories,
        // A draft saved while the Add Policies step was available may point
        // at step 2 — clamp to the last reachable step for this brokerage.
        currentStep: Math.min(draft.state.currentStep, lastStep) as WizardStepId,
      }));
      return true;
    } catch {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      return false;
    }
  }, [brokerageId, lastStep]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  }, []);

  const hasDraft = useCallback((): boolean => {
    try {
      const draftJson = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!draftJson) return false;

      const draft = JSON.parse(draftJson);
      const isExpired = Date.now() - draft.timestamp > DRAFT_EXPIRY_HOURS * 60 * 60 * 1000;
      const isDifferentBrokerage = draft.brokerageId !== brokerageId;

      return !isExpired && !isDifferentBrokerage;
    } catch {
      return false;
    }
  }, [brokerageId]);

  // Reset
  const resetWizard = useCallback(() => {
    setState(createInitialState());
    clearDraft();
  }, [clearDraft]);

  // Completion state management
  const setCompletionState = useCallback((completionState: CompletionState) => {
    setState(prev => ({ ...prev, completionState }));
  }, []);

  const updateEmailContent = useCallback((subject: string, body: string) => {
    setState(prev => {
      if (!prev.completionState) return prev;
      const defaultTemplate = prev.completionState.emailTemplate;
      const isModified = defaultTemplate
        ? subject !== defaultTemplate.subject || body !== defaultTemplate.editableBodyHtml
        : true;
      return {
        ...prev,
        completionState: {
          ...prev.completionState,
          customSubject: subject,
          customBody: body,
          isEmailModified: isModified,
        },
      };
    });
  }, []);

  const resetEmailToDefault = useCallback(() => {
    setState(prev => {
      if (!prev.completionState || !prev.completionState.emailTemplate) return prev;
      return {
        ...prev,
        completionState: {
          ...prev.completionState,
          customSubject: prev.completionState.emailTemplate.subject,
          customBody: prev.completionState.emailTemplate.editableBodyHtml,
          isEmailModified: false,
        },
      };
    });
  }, []);

  const setEmailTemplateLoading = useCallback((loading: boolean) => {
    setState(prev => {
      if (!prev.completionState) return prev;
      return {
        ...prev,
        completionState: {
          ...prev.completionState,
          emailTemplateLoading: loading,
        },
      };
    });
  }, []);

  const setEmailTemplate = useCallback((template: EmailTemplate) => {
    setState(prev => {
      if (!prev.completionState) return prev;
      return {
        ...prev,
        completionState: {
          ...prev.completionState,
          emailTemplate: template,
          customSubject: template.subject,
          customBody: template.editableBodyHtml,
          emailTemplateLoading: false,
        },
      };
    });
  }, []);

  const setSendingState = useCallback((sendingState: 'idle' | 'sending' | 'success' | 'error', error: string | null = null) => {
    setState(prev => {
      if (!prev.completionState) return prev;
      return {
        ...prev,
        completionState: {
          ...prev.completionState,
          sendingState,
          sendingError: error,
        },
      };
    });
  }, []);

  // Auto-save draft on state changes (debounced via useEffect)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (state.clients.length > 0 || state.policies.length > 0 || state.profileData.crmRelationshipNumber) {
        saveDraft();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [state.profileData, state.clients, state.policies, state.signatories, saveDraft]);

  const value: WizardContextType = {
    state,
    policiesStepEnabled,
    goToStep,
    nextStep,
    prevStep,
    canNavigateToStep,
    updateProfileData,
    addClient,
    updateClient,
    removeClient,
    setPrimaryClient,
    setSoloClientFormData,
    setCreatedProfile,
    addPolicy,
    updatePolicy,
    removePolicy,
    addSignatory,
    removeSignatory,
    updateSignatoryType,
    getSignatoriesForPolicy,
    validateStep,
    markStepTouched,
    registerBeforeLeaveAction,
    unregisterBeforeLeaveAction,
    setSubmitting,
    setSubmitError,
    saveDraft,
    loadDraft,
    clearDraft,
    hasDraft,
    setCompletionState,
    updateEmailContent,
    resetEmailToDefault,
    setEmailTemplateLoading,
    setEmailTemplate,
    setSendingState,
    resetWizard,
  };

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
}

export function useWizard() {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}
